import { useMemo } from 'react';
import type { WebhookProviderCatalogEntry } from '../types';
import {
  findWebhookProviderCatalogEntry,
  useWebhookProviderCatalog,
} from './use-webhook-provider-catalog';

export type WebhookEventOutputInfo = {
  outputShape: string | null;
  runOutputShape: string | null;
  outputSchema: Record<string, unknown> | null;
};

export function resolveWebhookEventOutputFromCatalog(
  catalog: WebhookProviderCatalogEntry | undefined,
  eventKey: string | undefined
): WebhookEventOutputInfo {
  if (catalog === undefined || eventKey === undefined || eventKey === '') {
    return {
      outputShape: null,
      runOutputShape: null,
      outputSchema: null,
    };
  }

  const event = catalog.events.find((candidate) => candidate.key === eventKey);
  if (event === undefined) {
    return {
      outputShape: null,
      runOutputShape: null,
      outputSchema: null,
    };
  }

  return {
    outputShape: event.outputShape ?? null,
    runOutputShape: event.runOutputShape ?? null,
    outputSchema: event.outputSchema ?? null,
  };
}

export function useWebhookEventOutput(params: {
  providerKey: string | undefined;
  eventKey: string | undefined;
}): {
  output: WebhookEventOutputInfo;
  isLoading: boolean;
} {
  const { providers, isLoading } = useWebhookProviderCatalog();
  const catalog = useMemo(() => {
    if (params.providerKey === undefined || params.providerKey === '') {
      return undefined;
    }
    return findWebhookProviderCatalogEntry(providers, params.providerKey);
  }, [providers, params.providerKey]);
  const output = useMemo(
    () => resolveWebhookEventOutputFromCatalog(catalog, params.eventKey),
    [catalog, params.eventKey]
  );

  return { output, isLoading };
}
